import { getFileUrl } from 'core/media/uploadPreparedMedia';
import { AttachmentCategory, ParsedAttachment } from './attachment.types';

export * from './attachment.constants';
export * from './attachment.types';

export const ATTACHMENT_SEPARATOR = '::';

const cleanBasename = (keyOrUrl: string): string => {
	if (!keyOrUrl) return '';
	try {
		const decoded = decodeURIComponent(keyOrUrl);
		const basename = decoded.split('/').pop() || decoded;
		const clean = basename.split('?')[0];
		const match = clean.match(/^[a-f0-9-]+_(.+)$/i) || clean.match(/^\d+-[a-z0-9]+-(.+)$/i);
		return match ? match[1] : clean;
	} catch {
		return keyOrUrl.split('/').pop() || keyOrUrl;
	}
};

export const parseAttachment = (raw: string): ParsedAttachment => {
	if (!raw) return { originalName: '', s3Key: '' };

	const separatorIndex = raw.lastIndexOf(ATTACHMENT_SEPARATOR);
	if (separatorIndex !== -1) {
		const originalName = raw.slice(0, separatorIndex).trim();
		const s3Key = raw.slice(separatorIndex + ATTACHMENT_SEPARATOR.length).trim();
		return {
			originalName: originalName || cleanBasename(s3Key),
			s3Key,
		};
	}

	return {
		originalName: cleanBasename(raw),
		s3Key: raw,
	};
};

export const encodeAttachment = (originalName: string, s3Key: string): string => {
	if (!originalName || originalName === s3Key) return s3Key;
	const safeName = originalName.replace(/::+/g, '_').trim();
	return `${safeName}${ATTACHMENT_SEPARATOR}${s3Key}`;
};

export const getAttachmentExtension = (fileOrKey: File | string): string => {
	const name = typeof fileOrKey === 'string' ? parseAttachment(fileOrKey).originalName : fileOrKey.name;
	if (!name) return '';
	const parts = name.split('?')[0].split('.');
	return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
};

export const getAttachmentCategory = (fileOrKey: File | string): AttachmentCategory => {
	if (typeof fileOrKey !== 'string' && fileOrKey.type) {
		if (fileOrKey.type === 'application/pdf') return 'pdf';
		if (
			fileOrKey.type.includes('word') ||
			fileOrKey.type === 'application/msword' ||
			fileOrKey.type.includes('officedocument.wordprocessingml')
		) {
			return 'doc';
		}
		if (
			fileOrKey.type.includes('excel') ||
			fileOrKey.type.includes('spreadsheet') ||
			fileOrKey.type === 'text/csv'
		) {
			return 'excel';
		}
		if (fileOrKey.type.startsWith('image/')) return 'image';
		if (fileOrKey.type.startsWith('video/')) return 'video';
		if (fileOrKey.type === 'text/plain') return 'text';
	}

	const ext = getAttachmentExtension(fileOrKey);
	switch (ext) {
		case 'pdf':
			return 'pdf';
		case 'doc':
		case 'docx':
		case 'rtf':
			return 'doc';
		case 'xls':
		case 'xlsx':
		case 'csv':
			return 'excel';
		case 'txt':
			return 'text';
		case 'jpg':
		case 'jpeg':
		case 'png':
		case 'webp':
		case 'gif':
		case 'heic':
		case 'heif':
		case 'svg':
			return 'image';
		case 'mp4':
		case 'mov':
		case 'webm':
		case 'mkv':
		case 'avi':
			return 'video';
		default:
			return 'file';
	}
};

export const getAttachmentDisplayName = (keyOrUrl: string): string => {
	if (!keyOrUrl) return '';
	return parseAttachment(keyOrUrl).originalName;
};

export const formatFileSize = (bytes?: number): string => {
	if (!bytes || bytes <= 0) return '';
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
	const mb = kb / 1024;
	return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
};

export const downloadAttachment = async (keyOrUrl: string, explicitFileName?: string): Promise<void> => {
	if (!keyOrUrl) return;
	const { originalName, s3Key } = parseAttachment(keyOrUrl);
	const targetKey = s3Key || keyOrUrl;
	const isHttp = targetKey.startsWith('http://') || targetKey.startsWith('https://');
	const resolvedUrl = isHttp ? targetKey : getFileUrl(targetKey);
	const targetFileName = explicitFileName || originalName || 'attachment';

	try {
		const response = await fetch(resolvedUrl, { mode: 'cors' });
		if (!response.ok) throw new Error('Fetch failed');
		const blob = await response.blob();
		const blobUrl = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = blobUrl;
		link.download = targetFileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(blobUrl);
	} catch {
		window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
	}
};
